/**
 * Mastery-style product page - all page behaviour
 * Copy to: assets/db-mastery.js
 *
 * Loaded by the ✨ Mastery Style section. Fully self-contained: this page does not
 * need daybreak.js or any other bundle's script.
 *
 * - Audio players (hero waveform, kit grid play buttons, kit player tracks)
 * - Kit player tabs + "Play next pack"
 * - Click-to-load video
 * - FAQ accordion
 * - Scroll animations
 */
(function () {
  'use strict';

  if (window.DBMasteryInitialized) return;
  window.DBMasteryInitialized = true;

  // ============================================
  // AUDIO PLAYER
  // Markup:  [data-dbm-player] wraps one player
  //            [data-dbm-play][data-dbm-src="…mp3"]   play/pause button
  //            [data-dbm-wave] > .dbm-wave__bar        optional waveform (click to seek)
  //            [data-dbm-time] / [data-dbm-time="elapsed"]  optional time readout
  // Only one player plays at a time across the whole page.
  // Audio files load only when someone presses play (nothing on page load).
  // ============================================
  var current = null;   // the [data-dbm-player] element that is playing
  var rafId = null;

  function formatTime(s) {
    if (!isFinite(s)) return '0:00';
    var m = Math.floor(s / 60);
    var sec = Math.floor(s % 60);
    return m + ':' + (sec < 10 ? '0' : '') + sec;
  }

  function getAudio(player) {
    if (player._dbmAudio) return player._dbmAudio;
    var btn = player.querySelector('[data-dbm-play]');
    var src = btn && btn.getAttribute('data-dbm-src');
    if (!src) return null;

    var audio = new Audio(src);
    audio.preload = 'auto';
    audio.addEventListener('loadedmetadata', function () { updateTime(player); });
    audio.addEventListener('ended', function () {
      stop(player);
      audio.currentTime = 0;
      updateProgress(player, 0);
      updateTime(player);
    });
    audio.addEventListener('error', function () { stop(player); });
    player._dbmAudio = audio;
    return audio;
  }

  function setPlayingState(player, playing) {
    player.classList.toggle('is-playing', playing);
    var btn = player.querySelector('[data-dbm-play]');
    if (btn) {
      btn.classList.toggle('is-playing', playing);
      btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
      var name = btn.getAttribute('data-dbm-name') || 'preview';
      btn.setAttribute('aria-label', (playing ? 'Pause ' : 'Play ') + name);
    }
  }

  function updateProgress(player, fraction) {
    var wave = player.querySelector('[data-dbm-wave]');
    if (!wave) return;
    var bars = wave._dbmBars || (wave._dbmBars = wave.querySelectorAll('.dbm-wave__bar'));
    var activeCount = Math.round(fraction * bars.length);
    if (wave._dbmActive === activeCount) return;
    var prev = wave._dbmActive || 0;
    var from = Math.min(prev, activeCount), to = Math.max(prev, activeCount);
    for (var i = from; i < to; i++) bars[i].classList.toggle('is-active', i < activeCount);
    wave._dbmActive = activeCount;
  }

  function updateTime(player) {
    var el = player.querySelector('[data-dbm-time]');
    var audio = player._dbmAudio;
    if (!el || !audio) return;
    if (el.getAttribute('data-dbm-time') === 'elapsed') {
      el.textContent = formatTime(audio.currentTime) + ' / ' + formatTime(audio.duration);
    } else {
      el.textContent = formatTime(audio.duration);
    }
  }

  function tick() {
    if (!current || !current._dbmAudio) return;
    var audio = current._dbmAudio;
    if (audio.duration) {
      updateProgress(current, audio.currentTime / audio.duration);
      updateTime(current);
    }
    rafId = requestAnimationFrame(tick);
  }

  function stop(player) {
    if (!player) return;
    if (player._dbmAudio) player._dbmAudio.pause();
    setPlayingState(player, false);
    if (current === player) {
      current = null;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function play(player) {
    var audio = getAudio(player);
    if (!audio) return;
    if (current && current !== player) stop(current);

    current = player;
    setPlayingState(player, true);
    var p = audio.play();
    if (p && p.catch) p.catch(function () { stop(player); });
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(tick);
  }

  function toggle(player) {
    if (current === player) stop(player);
    else play(player);
  }

  function seek(player, fraction) {
    var audio = getAudio(player);
    if (!audio) return;
    var apply = function () {
      if (audio.duration) {
        audio.currentTime = fraction * audio.duration;
        updateProgress(player, fraction);
        updateTime(player);
      }
    };
    if (audio.duration) apply();
    else audio.addEventListener('loadedmetadata', apply, { once: true });
    if (current !== player) play(player);
  }

  function stopWithin(scope) {
    if (current && (scope === document || scope.contains(current))) stop(current);
  }

  // ============================================
  // KIT PLAYER TABS
  // ============================================
  function wrapIndex(i, length) {
    return ((i % length) + length) % length;
  }

  function activateKit(root, index, autoplay) {
    if (!root) return;
    var tabs = root.querySelectorAll('[data-kit-tab]');
    var panels = root.querySelectorAll('[data-kit-panel]');
    if (!panels.length) return;

    index = wrapIndex(index, panels.length);
    stopWithin(root);

    tabs.forEach(function (tab, i) {
      var active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });

    panels.forEach(function (panel, i) {
      panel.hidden = i !== index;
    });

    // On mobile the tabs scroll sideways: bring the active one into view
    var activeTab = tabs[index];
    var list = activeTab && activeTab.parentElement;
    if (list && list.scrollWidth > list.clientWidth) {
      var offset = activeTab.getBoundingClientRect().left - list.getBoundingClientRect().left;
      list.scrollTo({ left: list.scrollLeft + offset - 16, behavior: 'smooth' });
    }

    if (autoplay) {
      var first = panels[index].querySelector('[data-dbm-player] [data-dbm-play]:not([disabled])');
      if (first) play(first.closest('[data-dbm-player]'));
    }
  }

  // ============================================
  // CLICK-TO-LOAD VIDEO (keeps page load fast)
  // ============================================
  function loadVideo(button) {
    var id = button.getAttribute('data-dbm-video-id');
    if (!id) return;

    var src = button.getAttribute('data-dbm-video') === 'vimeo'
      ? 'https://player.vimeo.com/video/' + encodeURIComponent(id) + '?autoplay=1&dnt=1'
      : 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&playsinline=1';

    var iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = button.getAttribute('aria-label') || 'Video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;

    stopWithin(document);
    button.replaceWith(iframe);
  }

  // ============================================
  // FAQ ACCORDION (for the db-faq-accordion section)
  // ============================================
  function toggleFaq(question) {
    // If another bundle's script is also on the page, let it handle the FAQ
    if (window.DaybreakInitialized) return;
    var item = question.closest('.db-faq-item');
    if (!item) return;
    var open = !item.classList.contains('is-open');
    document.querySelectorAll('.db-faq-item.is-open').forEach(function (other) {
      if (other !== item) {
        other.classList.remove('is-open');
        var q = other.querySelector('.db-faq-question');
        if (q) q.setAttribute('aria-expanded', 'false');
      }
    });
    item.classList.toggle('is-open', open);
    question.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  // ============================================
  // EVENTS
  // ============================================
  document.addEventListener('click', function (e) {
    var playBtn = e.target.closest('[data-dbm-play]');
    if (playBtn) {
      var player = playBtn.closest('[data-dbm-player]');
      if (player && !playBtn.disabled) toggle(player);
      return;
    }

    var wave = e.target.closest('[data-dbm-wave]');
    if (wave) {
      var wPlayer = wave.closest('[data-dbm-player]');
      if (wPlayer) {
        var rect = wave.getBoundingClientRect();
        var fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / (rect.width || 1)));
        seek(wPlayer, fraction);
      }
      return;
    }

    var tab = e.target.closest('[data-kit-tab]');
    if (tab) {
      if (tab.getAttribute('aria-selected') !== 'true') {
        activateKit(tab.closest('[data-dbm-kits]'), Number(tab.getAttribute('data-kit-tab')), false);
      }
      return;
    }

    var next = e.target.closest('[data-kit-next]');
    if (next) {
      var panel = next.closest('[data-kit-panel]');
      activateKit(next.closest('[data-dbm-kits]'), Number(panel.getAttribute('data-kit-panel')) + 1, true);
      return;
    }

    var facade = e.target.closest('[data-dbm-video]');
    if (facade) {
      loadVideo(facade);
      return;
    }

    var faq = e.target.closest('.db-faq-question');
    if (faq) toggleFaq(faq);
  });

  // Arrow keys move between kit tabs
  document.addEventListener('keydown', function (e) {
    var tab = e.target.closest && e.target.closest('[data-kit-tab]');
    if (!tab) return;
    var step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!step) return;

    e.preventDefault();
    var root = tab.closest('[data-dbm-kits]');
    var tabs = root.querySelectorAll('[data-kit-tab]');
    var index = wrapIndex(Number(tab.getAttribute('data-kit-tab')) + step, tabs.length);
    activateKit(root, index, false);
    tabs[index].focus();
  });

  // Starting a Shopify-hosted video stops any audio preview
  document.addEventListener('play', function (e) {
    if (e.target.closest && e.target.closest('.dbm-video')) stopWithin(document);
  }, true);

  // Theme editor: selecting a kit block in the sidebar shows that kit
  document.addEventListener('shopify:block:select', function (e) {
    var tab = e.target && e.target.closest && e.target.closest('[data-kit-tab]');
    if (tab) activateKit(tab.closest('[data-dbm-kits]'), Number(tab.getAttribute('data-kit-tab')), false);
  });

  // Theme editor: stop audio if its section is re-rendered or removed
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target) stopWithin(e.target);
  });

  // ============================================
  // SCROLL REVEAL
  // Elements with data-dbm-reveal fade/rise in as they enter the screen.
  // Only active when ✨ Mastery Style added the "dbm-anim" class (never in the theme editor).
  // ============================================
  function initReveal() {
    var root = document.documentElement;
    if (!root.classList.contains('dbm-anim')) return;

    var items = document.querySelectorAll('[data-dbm-reveal]');
    var show = function (el) { el.classList.add('is-revealed'); };

    if (!('IntersectionObserver' in window)) {
      items.forEach(show);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          show(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });

    items.forEach(function (el) { observer.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal);
  } else {
    initReveal();
  }

  // Small public API (used by nothing else yet, handy for future sections)
  window.DBMastery = { stopAll: function () { stopWithin(document); } };
})();